import type { ResultNode, AnswerRecord } from "../engine";
import { buildCriteriaStatus, formatAnswer } from "../summary/criteria";
import type { CriteriaItem, LabelResolver } from "../summary/criteria";

export const CLINICAL_DISCLAIMER =
  "This assessment is an informational decision-support aid. It does not provide medical advice " +
  "and is not a substitute for the professional clinical judgment of qualified personnel. Final " +
  "admission and clinical decisions rest with Big Bend Hospice clinical staff.";

export interface PdfMeta {
  conditionName: string;
  assessmentDate: string;
  functionalStatus?: string;
}

export interface PdfResponse {
  question: string;
  answer: string;
}

export interface PdfModel {
  title: string;
  conditionName: string;
  assessmentDate: string;
  determination: {
    title: string;
    score: number;
    level: ResultNode["determination"];
  };
  functionalStatus?: string;
  professional?: { label: string; value: string }[];
  criteria: CriteriaItem[];
  explanation: string;
  recommendations: string[];
  responses: PdfResponse[];
  disclaimer: string;
}

export function buildPdfModel(
  result: ResultNode,
  history: AnswerRecord[],
  meta: PdfMeta,
  computed: Record<string, number> = {},
  resolver?: LabelResolver
): PdfModel {
  return {
    title: "Hospice Eligibility Assessment",
    conditionName: meta.conditionName,
    assessmentDate: meta.assessmentDate,
    determination: {
      title: result.title,
      score: result.score,
      level: result.determination,
    },
    functionalStatus: meta.functionalStatus,
    criteria: buildCriteriaStatus(history, { computed, resolver }),
    explanation: result.explanation,
    recommendations: result.recommendations,
    responses: history.map((record) => ({
      question: record.question.text,
      answer: formatAnswer(record, resolver),
    })),
    disclaimer: CLINICAL_DISCLAIMER,
  };
}

export function pdfFilename(model: PdfModel): string {
  const sanitize = (s: string) => s.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `BigBendHospice-Eligibility-${sanitize(model.conditionName)}-${sanitize(model.assessmentDate)}.pdf`;
}
